import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Button, Checkbox, Dialog, DialogContent, DialogTitle, MenuItem, TextField } from "@mui/material";
import { api } from "../../../../api/client";
import { endpoints } from "../../../../api/endpoints";
import { rowsOf } from "../../../../api/services/records";
import { smsService, effectiveTarget } from "../../../../api/services/sms";
import useRemote from "../../../../hooks/useRemote";
import useMutation from "../../../../hooks/useMutation";
import { MigrationPage, Outcome } from "../../../shares/migration/MigrationWidgets";
const targets = { mother: "مادر", father: "پدر", both: "هر دو" };
function Target({ value, onChange, disabled }) {
  return <TextField select label="مقصد" value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled}>
    {Object.entries(targets).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
  </TextField>;
}
function SendFeedback({ mutation }) {
  return <>
    {mutation.error && <Alert severity="error">{mutation.error}</Alert>}
    {mutation.cooldown > 0 && <Alert severity="warning">امکان ارسال پس از {new Date(mutation.cooldown).toLocaleTimeString("fa-IR")}</Alert>}
    <Outcome data={mutation.result} />
  </>;
}
export function SmsButton({ kind, id }) {
  const [open, setOpen] = useState(false);
  return <><Button onClick={() => setOpen(true)}>پیامک نتایج</Button>
    {open && <Recipients kind={kind} id={id} close={() => setOpen(false)} />}
  </>;
}
function Recipients({ kind, id, close }) {
  const resource = useRemote((signal) => smsService.recipients(kind, id, signal), kind + ":" + id);
  const [selected, setSelected] = useState({});
  const mutation = useMutation({ uncertainOnFailure: true });
  useEffect(() => {
    setSelected(Object.fromEntries((resource.data?.recipients ?? []).filter((row) => row.sendable)
      .map((row) => [row.user_id, row.suggested_target || "mother"])));
  }, [resource.data]);
  const send = () => mutation.run((signal) => smsService.send(kind, id,
    (resource.data?.recipients ?? []).filter((row) => row.sendable && selected[row.user_id])
      .map((row) => ({ user_id: row.user_id, target: selected[row.user_id] })), signal));
  return <Dialog open onClose={mutation.busy ? undefined : close} fullWidth maxWidth="md" dir="rtl">
    <DialogTitle>گیرندگان پیامک نتایج</DialogTitle><DialogContent className="space-y-6">
      {resource.loading && <p role="status">دریافت گیرندگان…</p>}
      {resource.error && <Alert severity="error">{resource.error.message}</Alert>}
      {resource.data && <>
        <p>ارسال قبلی: {resource.data.sms_sent ? "انجام شده" : "انجام نشده"} · {resource.data.sms_sent_at || "—"}</p>
        {!resource.data.sms_permission && <Alert severity="info">مجوز ارسال هنوز صادر نشده است.</Alert>}
        <div className="overflow-auto"><table className="w-full text-right"><thead><tr>
          {["انتخاب", "دانش‌آموز", "نمره", "حضور", "مقصد", "مقصد مؤثر"].map((label) => <th key={label} className="p-3">{label}</th>)}
        </tr></thead><tbody>{resource.data.recipients.map((row) => <tr key={row.user_id}>
          <td><Checkbox inputProps={{ "aria-label": "انتخاب " + row.name }} checked={Boolean(selected[row.user_id])}
            disabled={!row.sendable || mutation.disabled || Boolean(mutation.result)} onChange={(_, checked) => setSelected({ ...selected, [row.user_id]: checked ? row.suggested_target || "mother" : null })} /></td>
          <td>{row.name}<br /><bdi>{row.username}</bdi></td><td>{row.score ?? "—"}</td><td>{row.present ? "حاضر" : "غایب"}</td>
          <td><Target value={selected[row.user_id] || row.suggested_target || "mother"}
            disabled={!row.sendable || mutation.disabled || Boolean(mutation.result)}
            onChange={(value) => setSelected({ ...selected, [row.user_id]: value })} /></td>
          <td>{targets[effectiveTarget(row, selected[row.user_id] || row.suggested_target)] || "شماره‌ای ندارد"}</td>
        </tr>)}</tbody></table></div>
      </>}
      <SendFeedback mutation={mutation} />
      <Button variant="contained" disabled={mutation.disabled || Boolean(mutation.result) || !resource.data?.sms_permission || !Object.values(selected).some(Boolean)} onClick={send}>ارسال همه انتخاب‌شده‌ها</Button>
      <Button disabled={mutation.busy} onClick={close}>بستن</Button>
    </DialogContent>
  </Dialog>;
}
function Picker({ url, label, value, onChange }) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => { const timer = setTimeout(() => setQuery(search), 350); return () => clearTimeout(timer); }, [search]);
  const data = useRemote(async (signal) => (await api.request({ url, params: { search: query }, safeRead: true, signal })).data, url + query);
  return <div>{data.error && <Alert severity="error">{data.error.message}</Alert>}<Autocomplete multiple
    options={rowsOf(data.data)} value={value} loading={data.loading} filterOptions={(options) => options}
    onInputChange={(_, input, reason) => { if (reason === "input") setSearch(input); }}
    onChange={(_, rows) => onChange(rows)} isOptionEqualToValue={(a, b) => a.id === b.id}
    getOptionLabel={(row) => row.name || [row.first_name, row.last_name, row.username].filter(Boolean).join(" ")}
    renderInput={(params) => <TextField {...params} label={label} />} /></div>;
}
export default function CustomSms() {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState("mother");
  const mutation = useMutation({ uncertainOnFailure: true });
  return <MigrationPage title="پیامک دلخواه">
    <Picker url={endpoints.management.groups} label="گروه‌ها" value={groups} onChange={setGroups} />
    <Picker url={endpoints.management.users} label="دانش‌آموزان" value={users} onChange={setUsers} />
    <TextField multiline minRows={4} label="متن پیام" value={message} onChange={(event) => setMessage(event.target.value)} />
    <Target value={target} onChange={setTarget} disabled={mutation.disabled} />
    <SendFeedback mutation={mutation} />
    <Button variant="contained" disabled={mutation.disabled || Boolean(mutation.result) || !message.trim() || (!groups.length && !users.length)}
      onClick={() => mutation.run((signal) => smsService.custom({ group_ids: groups.map((row) => row.id),
        user_ids: users.map((row) => row.id), message: message.trim(), target }, signal))}>ارسال پیام</Button>
  </MigrationPage>;
}
